import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  Alert,
  CircularProgress
} from '@mui/material';
import { format, subMonths, startOfMonth } from 'date-fns';
import useGridStore from '../../../stores/gridStore';

const CopyGridDialog = ({ open, onClose, currentMonth, projectId, platform = 'instagram', onCopy }) => {
  console.log('CopyGridDialog props:', { open, currentMonth, projectId, platform, currentMonthType: typeof currentMonth });
  
  const [selectedMonth, setSelectedMonth] = useState('');
  const [availableMonths, setAvailableMonths] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const { fetchGridsByRange } = useGridStore();

  useEffect(() => {
    if (open && projectId && currentMonth) {
      loadAvailableMonths();
    }
  }, [open, projectId, currentMonth]);

  const loadAvailableMonths = async () => {
    setIsLoading(true);
    try {
      console.log('loadAvailableMonths - currentMonth:', currentMonth);
      console.log('loadAvailableMonths - projectId:', projectId);
      
      // Validate currentMonth is a valid Date object
      if (!currentMonth || !(currentMonth instanceof Date) || isNaN(currentMonth.getTime())) {
        console.error('Invalid currentMonth:', currentMonth);
        setIsLoading(false);
        return;
      }
      
      // Get grids from last 6 months
      const endDate = startOfMonth(currentMonth);
      const startDate = subMonths(endDate, 6);
      
      console.log('Date calculations:', {
        currentMonth,
        endDate,
        startDate,
        endDateFormatted: format(endDate, 'yyyy-MM-dd'),
        startDateFormatted: format(startDate, 'yyyy-MM-dd')
      });
      
      const grids = await fetchGridsByRange(
        projectId, 
        format(startDate, 'yyyy-MM-dd'),
        format(endDate, 'yyyy-MM-dd')
      );

      console.log('Grids response:', grids);

      // Filter out current month and format for display
      const months = (grids || [])
        .filter(grid => grid && grid.month && format(new Date(grid.month), 'yyyy-MM') !== format(currentMonth, 'yyyy-MM'))
        .map(grid => ({
          value: format(new Date(grid.month), 'yyyy-MM-dd'),
          label: format(new Date(grid.month), 'MMMM yyyy'),
          tagCount: grid.tags?.length || 0,
          hasContent: grid.cells?.some(cell => cell.taskIds?.length > 0)
        }));

      setAvailableMonths(months);
    } catch (error) {
      console.error('Failed to load available months:', error);
    }
    setIsLoading(false);
  };

  const handleCopy = () => {
    if (selectedMonth) {
      onCopy(selectedMonth);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Copy Grid Structure</DialogTitle>
      
      <DialogContent>
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="caption">
            This will copy the grid layout and tags from another month. 
            Tasks will not be copied - only the structure.
          </Typography>
        </Alert>

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <FormControl fullWidth>
              <InputLabel>Select Month to Copy From</InputLabel>
              <Select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                label="Select Month to Copy From"
              >
                {availableMonths.length === 0 ? (
                  <MenuItem disabled>
                    <Typography variant="body2" color="textSecondary">
                      No previous grids found
                    </Typography>
                  </MenuItem>
                ) : (
                  availableMonths.map((month) => (
                    <MenuItem key={month.value} value={month.value}>
                      <Box sx={{ width: '100%' }}>
                        <Typography variant="body2">
                          {month.label}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {month.tagCount} tags
                          {month.hasContent && ' • Has content'}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>

            {selectedMonth && (
              <Box sx={{ mt: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                <Typography variant="subtitle2" gutterBottom>
                  What will be copied:
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  • Grid dimensions (number of rows)
                  <br />
                  • Cell mode (single/multiple tasks)
                  <br />
                  • All tags with their colors
                  <br />
                  • Cell tag assignments
                </Typography>
              </Box>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button 
          onClick={handleCopy} 
          variant="contained"
          disabled={!selectedMonth || isLoading}
          magical={true}
        >
          Copy Structure
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CopyGridDialog;